// Helpers for working with the YouTube URLs the API hands us for partner
// promo videos (`resort.video` / `resort.videoSummer`). The API returns
// whatever form the resort pasted in — watch?v=, youtu.be/, embed/ — so
// everything downstream goes through `youtubeVideoId` first.

// @see https://stackoverflow.com/a/8260383
const YOUTUBE_URL_RE = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;

/** Extract the 11-char video id from any common YouTube URL form, or `false`. */
export const youtubeVideoId = (url: string) => {
  const match = url.match(YOUTUBE_URL_RE);
  return (match && match[2].length === 11) ? match[2] : false;
};

/**
 * Static poster frame for a video id. `mqdefault` is the only size YouTube
 * serves at an exact 16:9 (320x180) — `hqdefault` is 4:3 with letterbox
 * bars baked into the JPEG, which would show as black edges in an
 * `aspect-video` box.
 */
export const youtubeThumbnailUrl = (videoId: string) => `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;

/**
 * Privacy-enhanced embed URL for a click-to-play player.
 *
 * `youtube-nocookie.com` is the same domain `useOneTrustIframe` rewrites
 * YouTube sources to; it sets no tracking cookies until the viewer
 * actually plays. `rel=0` keeps end-cards to the same channel and
 * `playsinline` stops iOS hijacking the whole screen.
 *
 * Autoplay rides on the click that opened the player: browsers grant
 * sticky user activation for a few seconds after a real gesture, so the
 * frame is allowed to start with sound. Where a browser declines (Firefox
 * blocks audible autoplay by default) YouTube falls back to its own play
 * button rather than failing.
 */
export const youtubeEmbedUrl = (videoId: string) => {
  const params = new URLSearchParams({
    autoplay: '1', rel: '0', playsinline: '1',
  });
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
};
